/* eslint-disable import/prefer-default-export */
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { forEach, isEmpty } from 'lodash';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import RetailerModel from '../../../../components/Retailers/RetailerModel';
import ShopOrderModel from '../../../../components/Orders/ShopOrderModel';

import { closeSidebar } from '../services/orderSidebarService';
import { buildOrderSchema } from '../schemas/orderSchema';
import { getDefaultFormValues } from '../utils/defaultFormValuesBuilder';

import { formatDataToSave } from '../../../../services/orderService';
import { normalizeCustomFieldsFromOrder } from '../services/orderCustomFieldsMapper';

import { fetchSelectCustomFields } from '../../../../actions/shops/customFields';
import { fetchActiveAgentsByRoleIfEmpty } from '../../../../actions/agents';
import { getAllSalesChannels } from '../../../../actions/shops/salesChannels';
import { addFakeEvent } from '../../../../services/CustomerEventsServices';
import { useCreateOrderNote } from './useCreateOrderNote';
import { usePhoneDisplay } from '../../../../hooks/usePhoneDisplay';
import { getMaskedPhone } from '../../../../services/phone/getMaskedPhone';
import { validateSelectedProducts } from '../validators/validateSelectedProducts';
import { buildLineItems, normalizeTerms } from '../services/orderFillService';

const stepFields = {
  1: ['customer.first_name', 'customer.last_name', 'customer.email', 'customer.country_id', 'customer.id_type', 'customer.id_number'],
  2: ['currency', 'status', 'agent_id', 'sales_channel_id'],
  3: ['payment_method']
};

export const useOrderForm = ({
  customer,
  selectedOrder,
  platform,
  action,
  createOrderMessage,
  t,
  tCommon,
  useForm
}) => {
  const dispatch = useDispatch();

  const {
    retailer_info,
    currentRetailerUser: currentRetailerUserInfo
  } = useSelector((state) => state.retailerUsersReducer);

  const { currentRetailerUser } = useSelector((state) => state.mainReducer);
  const { selectedCustomFields: customFields } = useSelector(
    (state) => state.customFieldsReducer
  );

  const [schema, setSchema] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [savingOrderLoading, setSavingOrderLoading] = useState(false);
  const [orderSaved, setOrderSaved] = useState({});
  const [saveAction, setSaveAction] = useState('');
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  const haveDeliveryMethods = retailer_info.shop_active_send_order
    || retailer_info.shop_active_store_pickup;

  const currency = retailer_info.currency_code || 'USD';
  const agentName = `${currentRetailerUser.first_name} ${currentRetailerUser.last_name}`;

  const baseSchema = useMemo(
    () => buildOrderSchema(tCommon, retailer_info.shipping_cost_method),
    [tCommon, retailer_info.shipping_cost_method]
  );

  const { maskedPhone } = usePhoneDisplay({
    phone: customer?.phone_without_country_code || selectedOrder.customer?.phone_without_country_code || '',
    user: currentRetailerUserInfo
  });

  const defaultFormValues = useMemo(
    () => getDefaultFormValues({
      customer,
      currentRetailerUser,
      maskedPhone,
      agentName,
      currency
    }),
    [customer, currentRetailerUser, maskedPhone, agentName, currency]
  );

  const methods = useForm({
    resolver: zodResolver(schema || baseSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldUnregister: false
  });

  const {
    getValues,
    setValue,
    setError,
    clearErrors,
    trigger,
    reset,
    control
  } = methods;

  const { createOrderNote } = useCreateOrderNote({
    orderSaved,
    retailerInfo: retailer_info,
    saveAction,
    getValues,
    createOrderMessage
  });

  const selectedProducts = useWatch({
    control,
    name: 'selectedProducts'
  });

  const deliveryMethod = useWatch({
    control,
    name: 'delivery_method'
  });

  // Limpiar error de selectedProducts cuando el usuario selecciona/deselecciona items
  useEffect(() => {
    if (selectedProducts) {
      clearErrors('selectedProducts');
    }
  }, [selectedProducts, clearErrors]);

  // Limpiar error de delivery_method cuando el usuario cambia el método de entrega
  useEffect(() => {
    if (deliveryMethod) {
      clearErrors('delivery_method');
    }
  }, [deliveryMethod, clearErrors]);

  // Iniciar carga de datos solo una vez
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true);
      const p1 = dispatch(fetchSelectCustomFields({ 'q[made_for_eq]': 0 }));
      const p2 = dispatch(fetchActiveAgentsByRoleIfEmpty());
      const p3 = dispatch(getAllSalesChannels());
      Promise.allSettled([p1, p2, p3]).finally(() => {
        setIsLoadingInitialData(false);
      });
    }
  }, [dispatch, hasInitialized]);

  useEffect(() => {
    if (!customFields) return;

    const dynamicSchema = customFields.reduce((acc, field) => {
      const fieldName = `${field.name}`;

      if (field.kind === 'date') {
        acc[fieldName] = field.mandatory_in_mercately
          ? z
            .date({ required_error: tCommon('errors.required') })
            .transform((d) => d.toISOString()) // o tu formato preferido
          : z
            .date().optional().transform((d) => d?.toISOString());
      } else if (field.kind === 'boolean') {
        acc[fieldName] = field.mandatory_in_mercately
          ? z.union([z.boolean(), z.undefined()]).refine(
            (val) => val === true,
            { message: tCommon('errors.required') }
          )
          : z.boolean().optional();
      } else {
        acc[fieldName] = field.mandatory_in_mercately
          ? z.string().min(1, tCommon('errors.required'))
          : z.string().optional();
      }

      return acc;
    }, {});

    setSchema(
      baseSchema.safeExtend({
        customFields: z.object(dynamicSchema).default({})
      })
    );

    reset(getValues());
  }, [customFields, baseSchema, reset, tCommon, getValues]);

  const fillOrderData = useCallback(() => {
    const itemsLine = buildLineItems(selectedOrder);
    const normalizedOrder = normalizeTerms(selectedOrder);
    const orderCustomer = normalizedOrder.customer || {};

    const {
      customFieldsValues,
      backendAnswers
    } = normalizeCustomFieldsFromOrder(normalizedOrder.custom_answers || []);

    const rawPhone = orderCustomer.phone ?? orderCustomer.phone_without_country_code ?? '';
    const displayPhone = getMaskedPhone({
      phone: rawPhone,
      user: currentRetailerUserInfo,
      masked: true
    });
    const customerForForm = {
      ...getValues('customer'),
      ...orderCustomer,
      ...(rawPhone && displayPhone !== rawPhone
        ? { phone: displayPhone, phone_without_country_code: displayPhone }
        : {})
    };

    reset({
      ...getValues(),
      ...normalizedOrder,
      customer: customerForForm,
      selectedProducts: itemsLine,
      customFields: customFieldsValues,
      customAnswers: backendAnswers,
      custom_answers: backendAnswers
    });
  }, [selectedOrder, reset, getValues, currentRetailerUserInfo]);

  const lastResetId = useRef();
  const hasInitializedTerms = useRef(false);

  useEffect(() => {
    const currentId = selectedOrder?.id || selectedOrder?.web_id || customer?.id;
    if (!currentId) return;

    if (lastResetId.current !== currentId) {
      hasInitializedTerms.current = false; // Reset terms initialization for new order
      if (isEmpty(selectedOrder)) {
        reset(defaultFormValues);
        setCurrentStep(1);
      } else {
        fillOrderData();
      }
      lastResetId.current = currentId;
    }
  }, [customer?.id, selectedOrder, reset, fillOrderData, defaultFormValues]);

  const closeModal = useCallback(() => {
    closeSidebar(dispatch, 'OrderModal');
  }, [dispatch]);

  useEffect(() => {
    if (isEmpty(orderSaved)) return;

    if (platform === 'facebook_comments' || platform === 'instagram_comments') {
      closeModal();
      return;
    }
    createOrderNote();

    dispatch({
      type: 'UPDATE_WHATSAPP_CUSTOMER',
      data: orderSaved.customer
    });

    setCurrentStep((prev) => prev + 1);

    if (saveAction === 'create') {
      dispatch(addFakeEvent('create_sale', currentRetailerUser));
    }

    setTimeout(() => {
      dispatch(addFakeEvent('create_order_note', currentRetailerUser));
    }, 300);
  }, [orderSaved]);

  useEffect(() => {
    if (!retailer_info || hasInitializedTerms.current) return;

    const orderTerms = selectedOrder?.terms_and_condition?.terms_conditions_content || retailer_info.terms_conditions_content;
    if (orderTerms) {
      setValue('terms_and_condition.terms_conditions_content', orderTerms);
    } else {
      setValue('update_terms', true);
    }

    hasInitializedTerms.current = true;
  }, [retailer_info, selectedOrder, setValue]);

  const updateCustomerObj = useCallback(() => {
    const orderFormData = getValues();

    setValue('customer', {
      ...getValues('customer'),
      address: orderFormData.address,
      city: orderFormData.city,
      state: orderFormData.state,
      zip_code: orderFormData.zip_code
    });
  }, [getValues, setValue]);

  const saveOrder = useCallback(() => {
    updateCustomerObj();

    const orderFormData = getValues();

    const formData = { ...orderFormData };
    formData.order_items = orderFormData.selectedProducts;
    formData.custom_answers = orderFormData.customAnswers;
    formData.removedOrderItems = orderFormData.removedSelectedProducts;

    const orderData = formatDataToSave(formData);

    if (orderFormData.update_terms) {
      const { terms_conditions_content } = orderFormData.terms_and_condition;
      if (terms_conditions_content?.length) {
        RetailerModel.updateTermsCondition(retailer_info.id, terms_conditions_content)
          .catch((err) => showtoast(err.error));
      }
    }

    setSavingOrderLoading(true);

    if (!isEmpty(selectedOrder)) {
      orderData.id = selectedOrder.web_id;
      setSaveAction('update');
    } else {
      setSaveAction('create');
    }

    orderData.id = selectedOrder.web_id;
    ShopOrderModel.save(orderData).then((res) => {
      showtoast(res.message);
      setOrderSaved(res.order);
      setSavingOrderLoading(false);
      // Limpiar caché de costos de envío cuando se guarda la orden
      dispatch({ type: 'CLEAR_SHIPPING_COSTS_CACHE' });
    }).catch((err) => {
      showtoast(err.error || err.message);
      setSavingOrderLoading(false);
    });
  }, [selectedOrder, retailer_info.id, dispatch, updateCustomerObj, getValues]);

  const onSubmit = useCallback(async () => {
    let valid = true;

    if (currentStep === 1) {
      let fieldsToValidate = [];

      if (haveDeliveryMethods) {
        fieldsToValidate.push('delivery_method');
        if (getValues('delivery_method') === 'send_order') {
          fieldsToValidate = [...fieldsToValidate, 'orderAddress', 'shipping_cost_id', 'shipping_value'];
        }
      }

      if (!customer?.ws_active) fieldsToValidate.push('customer.phone');

      const customFieldsToValidate = [];

      if (customFields) {
        forEach(customFields, (field) => {
          if (field.mandatory_in_mercately) {
            customFieldsToValidate.push(`customFields.${field.name}`);
          }
        });
      }

      valid = await trigger([...stepFields[currentStep], ...fieldsToValidate, ...customFieldsToValidate]);
    }

    if (currentStep === 2) {
      valid = await trigger([...stepFields[currentStep]]);
    }

    if (valid) {
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      }

      if (currentStep === 3) {
        const errorMessage = validateSelectedProducts(getValues('selectedProducts'));
        if (errorMessage) {
          setError('selectedProducts', {
            type: 'manual',
            message: t(`itemsBox.${errorMessage}`)
          });
          return;
        }

        saveOrder();
      }
    }
  }, [currentStep, haveDeliveryMethods, customer?.ws_active, customFields, trigger, getValues, setError, t, saveOrder]);

  const hadleBackAction = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      closeSidebar(dispatch, 'OrderModal');
    }
  }, [currentStep, dispatch]);

  const getTitle = useCallback(() => {
    if (currentStep === 1) {
      if (action === 'new') return t('modal.createOrder');
      return t("modal.editOrder");
    }
    if (currentStep === 2) return t('modal.titleStep2');
    if (currentStep === 3) return t('modal.titleStep3');

    return '';
  }, [currentStep, action, t]);

  const getSubmitLabel = useCallback(() => {
    if (currentStep === 3 && (platform === 'facebook_comments' || platform === 'instagram_comments')) {
      return tCommon('actions.save');
    }
    return tCommon('actions.continue');
  }, [currentStep, platform, tCommon]);

  const getCaption = useCallback(() => {
    if (currentStep === 1) return t('modal.caption1');
    if (currentStep === 2) return t('modal.caption2');
    if (currentStep === 3) return t('modal.caption3');

    return '';
  }, [currentStep, t]);

  return {
    currentStep,
    savingOrderLoading,
    orderSaved,
    saveAction,
    methods,
    getTitle,
    getCaption,
    getSubmitLabel,
    onSubmit,
    hadleBackAction,
    isLoadingInitialData,
    closeModal
  };
};
